import React, { useCallback, useContext, useEffect } from "react";
import classNames from "classnames";
import { Box, FlexBox, GridContainer, Row, Column, AlertBox, Spinner } from "@vp/swan";
import { RatingReviewData } from "@vp/rating-reviews-component";
import { ReferentialImage, ResolvedTab, SiteflowResponse } from "../../../../types/ContentfulTypes";
import { ProductBuilderData, ProductBuilderSelection } from "../../../../types/ProductBuilderTypes";
import ClientSidePriceRenderer from "./ClientSidePriceRenderer";
import SatisfactionGuaranteed from "./SatisfactionGuaranteed";
import OnPageBuilderHeader from "./OnPageBuilderHeader";
import { Placeholder } from "../../../../pluginsInfrastructure/PluginRegistryTypes";
import PluginPlaceholder from "../../../../pluginsInfrastructure/PluginPlaceholder";
import { isPhotobooks } from "../../../../utils/photobooksUtil";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";
// @ts-ignore
import "../styles/ProductBuilder.scss";
import "../styles/snowflake/ProductBuilder.scss";
import { ProductContext } from "../../../../contexts/ProductContextProvider";
import { useRequiredTextboxFieldsWithError } from "../../../../hooks/useRequiredTextboxFieldsWithError.hook";
import { UploadCtaClickedEventName } from "../../../../../src/plugins/Ctas/components/Ctas/UploadFlowCta";
import { PluginDataContext } from "../../../../../src/pluginsInfrastructure/PluginDataContextProvider";

type OnPageBuilderProps = {
  buildStateCtas?: { flows: SiteflowResponse[] };
  reviews: RatingReviewData;
  description?: string;
  locale: string;
  builderData: ProductBuilderData;
  productBuilderSelections: ProductBuilderSelection[];
  referentialImages?: ReferentialImage[];
  tabData: ResolvedTab[];
  hideReviews?: boolean;
  showCampaignCallouts?: boolean;
};

const OnPageBuilderContent = (props: OnPageBuilderProps) => {
  const {
    description,
    builderData,
    tabData,
    reviews,
    productBuilderSelections,
    showCampaignCallouts,
    hideReviews,
  } = props;
  const { updateInProgress, selections = {} } = useContext(ProductContext);
  const { mpv } = useContext(PageStaticDataContext);
  const { pluginData } = useContext(PluginDataContext);
  const requiredFieldsWithError = useRequiredTextboxFieldsWithError();
  const { name } = mpv;

  const [dismissRequiredInformationToast, setDismissRequiredInformationToast] = React.useState<
    boolean
  >(true);

  const requiredInformationErrorToast: string =
    pluginData.contentful["requiredInformationErrorToast"];
  const handleEvent = useCallback(() => {
    if (requiredFieldsWithError.length !== 0) {
      setDismissRequiredInformationToast(false);
      setTimeout(() => {
        setDismissRequiredInformationToast(true);
      }, 5000);
    }
  }, [requiredFieldsWithError, selections]);

  useEffect(() => {
    document.addEventListener(UploadCtaClickedEventName, handleEvent);
    return () => document.removeEventListener(UploadCtaClickedEventName, handleEvent);
  }, [handleEvent]);

  return (
    <Box className="on-page-builder">
      <GridContainer className="builder-container">
        <Box>
          <OnPageBuilderHeader
            builderData={builderData}
            reviews={reviews}
            productName={name}
            description={description}
            tabData={tabData}
            hideReviews={hideReviews}
            showCampaignCallouts={showCampaignCallouts}
          />
          <Box className="builder-controls">
            <div
              className={classNames("builder-step-loader", {
                "loader-active": updateInProgress,
              })}
            >
              <Spinner size="super" accessibleText="Fetching compatible options" />
            </div>
            <PluginPlaceholder placeholderType={Placeholder.ProductConfigurator} />
            <Row className={"price-row"}>
              <Column span={12} className="price-column">
                <FlexBox flexWrap="wrap">
                  <ClientSidePriceRenderer />
                  <PluginPlaceholder placeholderType={Placeholder.AfterPricing} />
                </FlexBox>
              </Column>
            </Row>
          </Box>
          {requiredInformationErrorToast && (
            <AlertBox skin="error" dismissed={dismissRequiredInformationToast} mt={"4"} toast>
              {requiredInformationErrorToast}
            </AlertBox>
          )}
          <PluginPlaceholder
            placeholderType={Placeholder.Ctas}
            customData={{ showDescription: true }}
          />
          <PluginPlaceholder placeholderType={Placeholder.AfterCtas} />
          <FlexBox flexDirection="column">
            <SatisfactionGuaranteed />
          </FlexBox>
        </Box>
      </GridContainer>
    </Box>
  );
};

export default OnPageBuilderContent;
